import { useEffect, useState } from 'react'
import { fetchJSON } from '@/pages/admin/_admin-utils'

type WalletSnapshot =
  | null
  | {
      wallet_context: string
      teacher_id?: string
      locker_room_id?: string
      balance_credits?: number
      balance_cents?: number
      budget_state?: any
      error?: string
      note?: string
    }

type Props = {
  ticketId: string
  bearer: Record<string, string> | null
}

export function TicketWalletSummary({ ticketId, bearer }: Props) {
  const [snapshot, setSnapshot] = useState<WalletSnapshot>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    if (!ticketId || !bearer?.Authorization) {
      setSnapshot(null)
      setLoading(false)
      setError('')
      return
    }

    let cancelled = false
    setLoading(true)
    setError('')

    fetchJSON(`/api/v1/admin/tickets/${ticketId}/wallet-snapshot`, {
      bearer,
    })
      .then((data) => {
        if (!cancelled) {
          setSnapshot(data as WalletSnapshot)
        }
      })
      .catch((err) => {
        if (cancelled) return
        setSnapshot(null)
        setError(err?.message || 'Unable to load wallet snapshot')
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false)
        }
      })

    return () => {
      cancelled = true
    }
  }, [bearer, ticketId])

  if (!ticketId) {
    return null
  }

  if (loading) {
    return <div className="text-sm text-slate-500">Loading wallet…</div>
  }

  if (error) {
    return <div className="text-sm text-brick">Wallet error: {error}</div>
  }

  if (!snapshot) {
    return <div className="text-sm text-slate-500">No wallet context.</div>
  }

  if (snapshot.error) {
    return <div className="text-sm text-red-600">Wallet error: {snapshot.error}</div>
  }

  if (snapshot.wallet_context === 'teacher') {
    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        <div className="font-medium">Teacher wallet</div>
        <div className="text-xs text-amber-700">Teacher ID: {snapshot.teacher_id}</div>
        <div className="mt-1">Credits: {snapshot.balance_credits ?? 0}</div>
        {snapshot.balance_cents != null ? (
          <div className="text-xs text-amber-700">Cash balance: ${(snapshot.balance_cents / 100).toFixed(2)}</div>
        ) : null}
      </div>
    )
  }

  if (snapshot.wallet_context === 'locker_room') {
    const budgetEntries = Array.isArray(snapshot.budget_state)
      ? snapshot.budget_state
      : []

    const totals = budgetEntries.reduce(
      (acc, entry) => {
        const remaining = Number(entry?.remaining_cents) || 0
        const plannedAnnual = Number(entry?.planned_annual_cents) || 0
        const plannedMonthly = Number(entry?.planned_monthly_cents) || 0
        const annualLimit = Number(entry?.annual_limit_cents) || 0

        acc.remaining_cents += remaining
        acc.planned_annual_cents += plannedAnnual
        acc.planned_monthly_cents += plannedMonthly
        acc.annual_limit_cents += annualLimit
        return acc
      },
      {
        remaining_cents: 0,
        planned_annual_cents: 0,
        planned_monthly_cents: 0,
        annual_limit_cents: 0,
      },
    )

    return (
      <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
        <div className="font-medium">Locker room wallet</div>
        <div className="text-xs text-amber-700">Locker room ID: {snapshot.locker_room_id}</div>
        <div className="mt-1">Credits: {snapshot.balance_credits ?? 0}</div>
        {snapshot.budget_state ? (
          <div className="mt-2 space-y-1 text-xs text-amber-700">
            <div>
              Total remaining: ${(totals.remaining_cents / 100).toFixed(2)}
            </div>
            <div>
              Planned annual: ${(totals.planned_annual_cents / 100).toFixed(2)}
            </div>
            <div>
              Planned monthly: ${(totals.planned_monthly_cents / 100).toFixed(2)}
            </div>
            <div>
              Annual limits: ${(totals.annual_limit_cents / 100).toFixed(2)}
            </div>
          </div>
        ) : null}
      </div>
    )
  }

  return (
    <div className="rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">
      <div className="font-medium">Wallet context: {snapshot.wallet_context}</div>
      <div className="text-xs text-amber-700">
        {snapshot.note || 'No wallet data available for this context.'}
      </div>
    </div>
  )
}

export default TicketWalletSummary
